import React from 'react';
import { X, Mic, Video, Volume2 } from 'lucide-react';
import { DeviceInfo } from '../hooks/useMediaDevices';

interface DeviceSelectorProps {
  audioDevices: DeviceInfo[];
  videoDevices: DeviceInfo[];
  selectedAudioId: string;
  selectedVideoId: string;
  onSelectAudio: (id: string) => void;
  onSelectVideo: (id: string) => void;
  onClose: () => void;
}

export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  audioDevices,
  videoDevices,
  selectedAudioId,
  selectedVideoId,
  onSelectAudio,
  onSelectVideo,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#121622] border border-slate-800 rounded-2xl shadow-2xl p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
          <h3 className="font-semibold text-white text-base">Audio & Video Settings</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Microphone */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-300 flex items-center space-x-2">
              <Mic className="w-4 h-4 text-blue-400" />
              <span>Microphone</span>
            </label>
            <select
              value={selectedAudioId}
              onChange={(e) => onSelectAudio(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white focus:outline-none focus:border-blue-500 transition"
            >
              {audioDevices.length === 0 ? (
                <option value="">Default Microphone</option>
              ) : (
                audioDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Camera */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-300 flex items-center space-x-2">
              <Video className="w-4 h-4 text-blue-400" />
              <span>Camera</span>
            </label>
            <select
              value={selectedVideoId}
              onChange={(e) => onSelectVideo(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white focus:outline-none focus:border-blue-500 transition"
            >
              {videoDevices.length === 0 ? (
                <option value="">Default Camera</option>
              ) : (
                videoDevices.map((d) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Speaker Info */}
          <div className="space-y-1.5 pt-2">
            <label className="text-xs font-semibold text-slate-300 flex items-center space-x-2">
              <Volume2 className="w-4 h-4 text-blue-400" />
              <span>Speaker / Audio Output</span>
            </label>
            <p className="text-xs text-slate-400 bg-slate-900/50 p-3 rounded-xl border border-slate-800/80">
              Audio is routed through your system's default audio output device.
            </p>
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-semibold shadow-md transition"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
